import {useRef, useState} from 'react';
import {Link, Navigate, useLocation} from 'react-router';
import {useAuth} from '../hooks/AuthContext';
import {intendedPath} from '../auth/redirect';
import {providerErrorMessage} from '../auth/social';
import {MESSAGES} from '../auth/signup';
import {sessionEndedNotice} from '../auth/sessionNotice';
import AuthLayout from '../components/auth/AuthLayout';
import PasswordField from '../components/auth/PasswordField';
import SsoButtons from '../components/auth/SsoButtons';
import TermsAgreement from '../components/auth/TermsAgreement';
import './Login.css';

const ERROR_ID = 'auth-error';

/**
 * Legacy AIDentalBook sign-in page: email/phone/username + password (POST /api/v1/auth/login),
 * Google / Apple / Facebook through the providers' JS SDKs (token -> POST /api/v1/auth/social),
 * and the Terms/Privacy agreement that must be ticked before either is used.
 */
export default function Login() {
  const auth = useAuth();
  const location = useLocation();
  const from = intendedPath(location.state);
  // A notice from the router state, or the "session ended" reason the API client sets on a 401.
  const notice = (location.state && location.state.notice) || sessionEndedNotice(location.search);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [termsInvalid, setTermsInvalid] = useState(false);
  const [credentialsInvalid, setCredentialsInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const termsRef = useRef(null);

  if (auth.user) return <Navigate to={from} replace />;

  /** True when the agreement is ticked; otherwise explains why and moves focus to it. */
  const requireTerms = () => {
    if (agreed) return true;
    setCredentialsInvalid(false);
    setTermsInvalid(true);
    setError(MESSAGES.terms);
    if (termsRef.current) termsRef.current.focus();
    return false;
  };

  const onAgree = (checked) => {
    setAgreed(checked);
    if (checked && termsInvalid) {
      setTermsInvalid(false);
      setError('');
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || !requireTerms()) return;
    setError('');
    setCredentialsInvalid(false);
    setBusy(true);
    try {
      await auth.loginWithPassword(identifier, password);
    } catch (err) {
      setCredentialsInvalid(true);
      setError(err.message || 'Sign-in failed. Please try again.');
      setBusy(false);
    }
  };

  /** Provider token -> session; on success `auth.user` is set and <Navigate> takes over. */
  const onSocialToken = async (provider, payload) => {
    if (busy) return;
    setError('');
    setCredentialsInvalid(false);
    setBusy(true);
    try {
      await auth.loginWithSocial({provider, ...payload});
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const onProviderError = (provider, kind) => {
    setCredentialsInvalid(false);
    setError(providerErrorMessage(provider, kind));
  };

  const credentialDescribedBy = credentialsInvalid ? ERROR_ID : undefined;

  return (
    <AuthLayout>
      <div className="auth-heading">
        <p className="auth-eyebrow">WELCOME</p>
        <h1 className="auth-title">Sign in.</h1>
      </div>

      {notice && (
        <p className="auth-notice" role="status" data-testid="login-notice">
          {notice}
        </p>
      )}

      <form className="auth-panel" onSubmit={submit} aria-label="Sign in">
        <div className="auth-fields">
          <div className="auth-field">
            <label htmlFor="login-identifier" className="auth-label">
              Email, phone or username
            </label>
            <input
              id="login-identifier"
              name="identifier"
              type="text"
              className="auth-input"
              placeholder="name@example.com"
              autoComplete="username"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              aria-invalid={credentialsInvalid ? 'true' : undefined}
              aria-describedby={credentialDescribedBy}
              data-testid="login-identifier"
            />
          </div>
          <PasswordField
            id="login-password"
            name="password"
            label="Password"
            toggleLabel="password"
            placeholder="••••••••"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={credentialsInvalid ? 'true' : undefined}
            aria-describedby={credentialDescribedBy}
            data-testid="login-password"
          />
        </div>

        <p className="auth-forgot">
          <a className="auth-link" href="/forget-password">
            Forgot password?
          </a>
        </p>

        <p className="auth-or">or continue with</p>
        <SsoButtons
          verb="Sign in"
          testIdPrefix="login-sso"
          locked={!agreed}
          busy={busy}
          describedBy={termsInvalid ? ERROR_ID : undefined}
          onLocked={requireTerms}
          onToken={onSocialToken}
          onProviderError={onProviderError}
        />

        <TermsAgreement
          ref={termsRef}
          id="login-terms"
          checked={agreed}
          onChange={onAgree}
          invalid={termsInvalid}
          describedBy={termsInvalid ? ERROR_ID : undefined}
        />

        <div id={ERROR_ID} className="auth-alert" role="alert" aria-atomic="true" data-testid="auth-error">
          {error}
        </div>

        <button type="submit" className="auth-submit" disabled={busy} data-testid="login-submit">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="auth-switch">
          New to AIDentalBook?{' '}
          <Link to="/signup" className="auth-link">
            Create an account
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
